import {useCallback, useState, useEffect} from 'react';
import clsx from 'clsx';
import {Image} from '@shopify/hydrogen';
import ReactSlider from 'react-slider';
import CurrencyInput from 'react-currency-input-field';
import {
  useRefinementList,
  UseRefinementListProps,
  RefinementListItem,
  useRange,
  UseRangeSliderProps,
} from 'react-instantsearch';
import {DATA_TEST_IDS} from '~/lib/const';
import iconArrow from '../../assets/icons/icon-arrow.svg';
import {IconCheckV2} from '~/components';

type FilterOption = {
  name: string;
  type: string; //'options' | 'range'
};

export const PRODUCT_FILTERS = [
  {
    filter: {name: 'Brand', type: 'options'},
    attribute: 'vendor',
    showMore: true,
  },
  {
    filter: {name: 'Year', type: 'options'},
    attribute: 'named_tags.year',
    limit: 5,
    showMore: true,
  },
  {
    filter: {name: 'Price', type: 'range'},
    isLast: true,
    attribute: 'price',
  },
];

const ProductRefinementList = (props: UseRefinementListProps) => {
  const {showMore} = props;
  const {items, refine, canToggleShowMore, isShowingMore, toggleShowMore} =
    useRefinementList(props);

  return (
    <ul className="font-header text-18/30 font-bold space-y-2.5 mb-[22px]">
      {items.map((item: RefinementListItem) => (
        <li key={item.label}>
          <button
            className="flex items-start space-x-2.5"
            onClick={() => refine(item.value)}
          >
            <div
              className={clsx(
                'flex justify-center items-center w-[30px] h-[30px] border border-gray-100 rounded-[5px]',
                item.isRefined ? 'bg-blue-500' : 'bg-white',
              )}
            >
              {item.isRefined && (
                <IconCheckV2 data-testid={DATA_TEST_IDS.CHECKBOX('checked')} />
              )}
            </div>
            <div className="uppercase">
              <span>{item.label}</span>
            </div>
          </button>
        </li>
      ))}
      {showMore && (
        <button onClick={toggleShowMore} disabled={!canToggleShowMore}>
          {isShowingMore ? 'Show less' : 'Show more'}
        </button>
      )}
    </ul>
  );
};

const PRICE_RANGE_ARIA_LABEL = ['Lower thumb', 'Upper thumb'];
const PRICE_RANGE_ARIA_LABEL_VALUE_TEXT = (state: {valueNow: number}) =>
  `Thumb value ${state.valueNow}`;

const ProductRange = (props: UseRangeSliderProps) => {
  const {start, range, refine} = useRange(props);
  const {min, max} = range;
  const [value, setValue] = useState({start: min, end: max});

  const from = Math.max(min, Number.isFinite(start[0]) ? start[0] : min);
  const to = Math.min(max, Number.isFinite(start[1]) ? start[1] : max);

  useEffect(() => {
    setValue({start: from, end: to});
  }, [from, to]);

  const handleValueChangeRangeMinInput = useCallback(
    (value?: string) => {
      setValue((prev) => ({
        start: Number(value ?? 0),
        end: prev.end,
      }));
    },
    [setValue],
  );

  const handleValueChangeRangeMaxInput = useCallback(
    (value?: string) => {
      setValue((prev) => ({
        start: prev.start,
        end: Number(value ?? 0),
      }));
    },
    [setValue],
  );

  const handleChangeRange = useCallback(
    (values: number[]) => {
      setValue(() => ({
        start: values[0],
        end: values[1],
      }));
    },
    [setValue],
  );

  return (
    <div className="mb-[30px]">
      <ReactSlider
        className="w-100 h-[22px] flex items-center"
        thumbClassName="w-[22px] h-[22px] bg-blue-500 rounded-full"
        trackClassName="slider-track"
        value={[value.start, value.end]}
        min={min ?? 0}
        max={max ?? 10000}
        ariaLabel={PRICE_RANGE_ARIA_LABEL}
        ariaValuetext={PRICE_RANGE_ARIA_LABEL_VALUE_TEXT}
        pearling
        minDistance={1}
        onChange={handleChangeRange}
        onAfterChange={() => refine([value.start, value.end])}
      />
      <div className="flex justify-between items-center mt-5">
        <CurrencyInput
          className="h-11 w-[77px] border border-gray-100 text-center font-medium text-18/26 p-2"
          value={value.start}
          decimalsLimit={0}
          min={min ?? 0}
          max={max ?? 10000}
          prefix={'$'}
          onValueChange={handleValueChangeRangeMinInput}
          onBlur={() => refine([value.start, value.end])}
        />

        <CurrencyInput
          className="h-11 w-[77px] border border-gray-100 text-center font-medium text-18/26 p-2"
          value={value.end}
          decimalsLimit={0}
          min={min ?? 0}
          max={max ?? 10000}
          prefix={'$'}
          onValueChange={handleValueChangeRangeMaxInput}
          onBlur={() => refine([value.start, value.end])}
        />
      </div>
    </div>
  );
};

export const ProductFilter = ({
  filter,
  isLast,
  ...algoliaProps
}: {
  filter: FilterOption;
  isLast?: boolean;
} & UseRefinementListProps) => {
  const [expanded, setExpanded] = useState(false);

  const handleClickExpand = useCallback(() => {
    setExpanded((prev) => !prev);
  }, [setExpanded]);

  return (
    <div
      className={clsx(
        'border-t border-t-gray-300',
        isLast && 'border-b border-b-gray-300',
      )}
    >
      <button
        data-testid={DATA_TEST_IDS.BUTTON(filter.name)}
        type="button"
        className="flex w-full justify-between items-center py-5"
        aria-label={`Toggle ${filter.name} filter`}
        onClick={handleClickExpand}
      >
        <span className="font-header text-22/32 font-bold">{filter.name}</span>
        <Image
          loading="lazy"
          src={iconArrow}
          width={16.58}
          height={9.71}
          alt={filter.name}
          className={clsx('transition-all mr-2', {
            'rotate-180': expanded,
          })}
        />
      </button>
      <div
        className={clsx(
          'overflow-auto transition-max-height duration-500 ease-in-out',
          expanded ? 'max-h-[500px]' : 'max-h-0',
        )}
      >
        {filter.type === 'options' && (
          <ProductRefinementList {...algoliaProps} />
        )}
        {filter.type === 'range' && <ProductRange {...algoliaProps} />}
      </div>
    </div>
  );
};
